import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Button, DialogActions } from '@mui/material';
import { useSnackbar } from 'notistack';
import * as Yup from 'yup';

import { closeDialog } from '@/components/Dialog';
import { useConnectedFormComponents } from '@/components/form';
import { parseMaintenanceDatesISOToDuration } from '@/helpers/maintenances';
import AppError from '@/interfaces/AppError';
import { IMaintenance } from '@/interfaces/maintenance/IMaintenance';
import maintenancesService from '@/services/maintenances';

type Form = {
  description: string;
};

const schema = Yup.object().shape({
  description: Yup.string(),
});

interface Props {
  maintenance: IMaintenance;
  onUpdate(updatedMaintenance: IMaintenance): void;
}

/**
 * Lets staff correct or extend the reported problem after the appointment was
 * booked. Customers routinely call back with further symptoms, and the calendar
 * only allows editing an appointment while it is still PENDING.
 */
export default function MaintenanceNotesForm({ maintenance, onUpdate }: Props) {
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);

  const { enqueueSnackbar } = useSnackbar();

  const { handleSubmit, control, ...formHook } = useForm<Form>({
    defaultValues: { description: maintenance.description || '' },
    resolver: yupResolver(schema),
  });

  const { TextInput } = useConnectedFormComponents<Form>({ control });

  const onSubmit = async (data: Form) => {
    try {
      setSubmitLoading(true);

      // The edit endpoint takes a full payload, so the scheduling fields are
      // echoed back unchanged and only the notes differ.
      const updatedMaintenance = await maintenancesService.editMaintenance({
        maintenanceId: maintenance.id,
        vehicleId: maintenance.vehicle.vin,
        maintenanceTypeId: maintenance.type.id,
        startDate: new Date(maintenance.expectedStartDate),
        duration: parseMaintenanceDatesISOToDuration(
          maintenance.expectedStartDate,
          maintenance.expectedEndDate
        ),
        description: data.description,
        subscriptionVehicleId: maintenance.subscriptionVehicle?.id,
      });

      onUpdate(updatedMaintenance);
      closeDialog();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(
        error instanceof AppError
          ? error?.message
          : 'Error saving notes. Please, try again later',
        { variant: 'error' }
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <FormProvider control={control} handleSubmit={handleSubmit} {...formHook}>
      <Box
        sx={{ width: { sm: '450px' } }}
        component="form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextInput
          label="Reported problem / notes"
          fieldName="description"
          fullWidth
          multiline
          minRows={4}
          helperText="Visible to whoever performs the repair"
        />

        <DialogActions>
          <Button onClick={closeDialog} disabled={submitLoading}>
            Cancel
          </Button>

          <Button
            type="submit"
            color="primary"
            disabled={submitLoading}
            onClick={handleSubmit(onSubmit)}
          >
            {submitLoading ? 'Loading...' : 'Save'}
          </Button>
        </DialogActions>
      </Box>
    </FormProvider>
  );
}
